import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import type {
  GetWorklistCustomStatusesQuery,
  GetWorklistCustomStatusesQueryVariables,
  WorklistCustomStatus,
} from 'generated/graphql';
import {
  GET_WORKLIST_CUSTOM_STATUSES,
  CHANGE_WORKLIST_ITEM_CUSTOM_STATUS,
} from 'modules/Apollo/queries';
import { useCallback, useMemo, useRef } from 'react';
import { logger } from '../modules/logger';
import {
  CrossWindowDataRefetchType,
  NameSpaces,
  sendCrossTabEvent,
} from '../modules/EventsManager/eventsManager';
import { useToasterDispatch } from '../common/ui/Toaster/Toaster';
import type { MultiSelectFilterItemType } from '../domains/worklist/WorklistFilters/types';
import { useBroadcastRefreshCurrentCase } from 'common/CurrentCase/useBroadcastRefreshCurrentCase';
import { WithApolloContext } from 'modules/Apollo/types';
import { ToastKey } from 'common/ui/Toaster/Toast';
import { PageTypes } from 'utils/pageTypes';

export const formatStatusName = (name: string): string => {
  if (!name) {
    return '';
  }

  const wordsNotToCapitalize = ['to', 'and', 'for', 'of', 'on', 'at', 'with', 'by', 'from'];

  return name
    .toLowerCase()
    .split('_')
    .map((word, index) =>
      wordsNotToCapitalize.includes(word) && index !== 0
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(' ');
};

const createFilterObject = (statuses: Nullable<ReadonlyArray<WorklistCustomStatus>>) => {
  if (statuses == null) {
    return [];
  }

  return statuses
    .filter((status) => status.smid)
    .map((status) => ({ value: status.smid, label: formatStatusName(status.name) }));
};

type LogProps = {
  currentCaseId: string;
  customStatus: Nullable<Omit<WorklistCustomStatus, '__typename'>>;
  groupId: Nullable<string>;
};

type UpdateCustomStatusProps = {
  currentCaseId: string;
  customStatusID: Nullable<string>;
  accessionNumber: string;
  groupId: Nullable<string>;
  isClaimedByMe: boolean;
};

type UseWorklistCustomStatusReturn = {
  customStatuses: Array<WorklistCustomStatus>;
  customStatusFilters: MultiSelectFilterItemType[];
  isLoadingUpdateCustomStatus: boolean;
  isLoadingCustomStatuses: boolean;
  updateCustomStatus: (arg1: UpdateCustomStatusProps) => Promise<void>;
};

export function useWorklistCustomStatuses({
  context,
  skipQuery = false,
}: WithApolloContext & { skipQuery?: boolean }): UseWorklistCustomStatusReturn {
  const client = useApolloClient();
  const broadcastRefreshCase = useBroadcastRefreshCurrentCase();
  const { enqueueOrUpdateToast } = useToasterDispatch();
  const toastKey = useRef<Nullable<ToastKey>>('');

  const { data: customStatusesData, loading: isLoadingCustomStatuses } = useQuery<
    GetWorklistCustomStatusesQuery,
    GetWorklistCustomStatusesQueryVariables
  >(GET_WORKLIST_CUSTOM_STATUSES, { context, skip: skipQuery });

  const rawCustomStatuses = useMemo(() => {
    return customStatusesData?.worklistCustomStatuses ?? [];
  }, [customStatusesData]);

  const customStatuses = useMemo(() => {
    const statuses = customStatusesData?.worklistCustomStatuses ?? [];
    return statuses.map((status) => ({ ...status, name: formatStatusName(status.name) }));
  }, [customStatusesData]);

  const customStatusFilters = useMemo(
    () => createFilterObject(customStatusesData?.worklistCustomStatuses),
    [customStatusesData?.worklistCustomStatuses]
  );

  const [changeWorklistItemCustomStatus, { loading: isLoadingUpdateCustomStatus }] = useMutation(
    CHANGE_WORKLIST_ITEM_CUSTOM_STATUS,
    { context }
  );

  const writeCustomStatusToCache = useCallback(
    (worklistItemSmid: string, next: Nullable<Omit<WorklistCustomStatus, '__typename'>>) => {
      const id = client.cache.identify({ __typename: 'WorklistItem', smid: worklistItemSmid });
      if (!id) {
        return;
      }

      client.cache.modify({
        id,
        fields: {
          customStatus: () => {
            if (!next?.smid) {
              return null;
            }

            return client.cache.writeFragment({
              fragment: gql`
                fragment _WorklistCustomStatusPatch on WorklistCustomStatus {
                  smid
                  name
                  __typename
                }
              `,
              data: {
                __typename: 'WorklistCustomStatus',
                smid: next.smid,
                name: next.name,
              },
            });
          },
        },
      });
    },
    [client.cache]
  );

  const broadcastPatch = useCallback(
    (worklistItemSmid: string, next: Nullable<Omit<WorklistCustomStatus, '__typename'>>) => {
      sendCrossTabEvent(NameSpaces.WORKLIST_ITEM_PATCH, {
        type: 'worklistItemPatch',
        targetPage: PageTypes.WORKLIST,
        data: {
          id: worklistItemSmid,
          patch: {
            customStatus: next?.smid
              ? {
                  smid: next.smid,
                  name: next.name,
                  retainUserAssignment: next.retainUserAssignment,
                  retainOnExamComplete: next.retainOnExamComplete,
                }
              : null,
          },
        },
      });
    },
    []
  );

  const logError = useCallback((props: LogProps) => {
    const { currentCaseId, customStatus, groupId } = props;
    const nextName = customStatus?.name ?? 'N/A';

    if (groupId == null) {
      logger.error(
        `[useWorklistCustomStatus] - Error occurred when changing custom status of case ${currentCaseId} to "${nextName}"`,
        {
          currentCaseId,
          customStatusID: customStatus?.smid,
        }
      );
      return;
    }

    logger.error(
      `[useWorklistCustomStatus] - Error occurred when changing custom status of group ${groupId}, triggered by case ${currentCaseId}, to "${nextName}"`,
      {
        currentCaseId,
        customStatusID: customStatus?.smid,
        groupId,
      }
    );
  }, []);

  const logInfo = useCallback((props: LogProps) => {
    const { currentCaseId, customStatus, groupId } = props;
    const nextName = customStatus?.name ?? 'N/A';

    if (groupId == null) {
      logger.info(
        `[useWorklistCustomStatus] - Custom status of case ${currentCaseId} changed to "${nextName}"`,
        {
          currentCaseId,
          customStatusID: customStatus?.smid,
        }
      );
      return;
    }

    logger.info(
      `[useWorklistCustomStatus] - Custom status of group ${groupId}, triggered by case ${currentCaseId}, changed to "${nextName}"`,
      {
        currentCaseId,
        customStatusID: customStatus?.smid,
        groupId,
      }
    );
  }, []);

  const updateCustomStatus = useCallback(
    async (props: UpdateCustomStatusProps) => {
      const { currentCaseId, customStatusID, accessionNumber, groupId, isClaimedByMe } = props;
      const next = rawCustomStatuses.find((status) => status.smid === customStatusID);

      await changeWorklistItemCustomStatus({
        variables: {
          smid: currentCaseId,
          customStatusID: next?.smid ?? null,
        },
        optimisticResponse: {
          changeWorklistItemCustomStatus: {
            __typename: 'WorklistItem',
            smid: currentCaseId,
            customStatus: next?.smid ? next : null,
          },
        },
        update: () => {
          writeCustomStatusToCache(currentCaseId, next);
        },
        onError: () => {
          logError({
            groupId,
            currentCaseId,
            customStatus: next,
          });
          toastKey.current = enqueueOrUpdateToast(
            `An error occurred when changing the workflow status for ${accessionNumber ?? 'N/A'}. Please try again.`,
            toastKey.current,
            { severity: 'error', position: 'top-right', yOffset: 45 }
          );
        },
        onCompleted: async () => {
          logInfo({
            groupId,
            currentCaseId,
            customStatus: next,
          });

          await broadcastRefreshCase();
          broadcastPatch(currentCaseId, next);

          const shouldRefetchMyQueue = isClaimedByMe && next?.retainUserAssignment === false;

          if (shouldRefetchMyQueue) {
            sendCrossTabEvent(NameSpaces.CROSS_WINDOW_DATA_REFETCH, {
              type: CrossWindowDataRefetchType.CLAIMED_ITEMS,
            });
          }

          const nextDisplayName = next?.name ? formatStatusName(next.name) : 'N/A';
          let toastMessage = `Updated workflow status for ${accessionNumber ?? 'N/A'} to "${nextDisplayName}".`;
          if (shouldRefetchMyQueue) {
            toastMessage +=
              groupId != null
                ? ' The exam and its grouped exams were removed from your queue.'
                : ' The exam was removed from your queue.';
          }

          toastKey.current = enqueueOrUpdateToast(toastMessage, toastKey.current, {
            severity: 'default',
            position: 'top-right',
            icon: 'greenCheck',
            autoHideDuration: 4000,
            yOffset: 45,
          });
        },
      });
    },
    [
      rawCustomStatuses,
      changeWorklistItemCustomStatus,
      writeCustomStatusToCache,
      logError,
      enqueueOrUpdateToast,
      logInfo,
      broadcastRefreshCase,
      broadcastPatch,
    ]
  );

  return useMemo(
    () => ({
      customStatuses,
      customStatusFilters,
      updateCustomStatus,
      isLoadingUpdateCustomStatus,
      isLoadingCustomStatuses,
    }),
    [
      customStatuses,
      customStatusFilters,
      updateCustomStatus,
      isLoadingUpdateCustomStatus,
      isLoadingCustomStatuses,
    ]
  );
}
